import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Card,
  Divider,
  Tabs,
  Tab,
  Button,
  alpha,
  useTheme,
  CircularProgress,
  Tooltip,
  Stack,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Storage as DiskIcon,
  Speed as CpuIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Article as LogsIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { format, fromUnixTime } from 'date-fns';
import esdlService from '../../services/esdlService';
import EsdlSummaryChart from './EsdlSummaryChart';
import ErrorLogsTable from './ErrorLogsTable';
import { throttle } from 'lodash';

/**
 * Host Details Dashboard Component
 * Displays detailed monitoring information for a single host
 */
const HostDetailsDashboard = ({ hostname }) => {
  // Add requestInProgress ref to track and throttle API calls
  const requestInProgress = useRef(false);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [hostData, setHostData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('24h');
  
  // Fetch host details with throttling and caching
  const fetchHostDetails = useCallback(async () => {
    if (!hostname) return;
    
    // Prevent duplicate requests
    if (requestInProgress.current) {
      console.log('A host details request is already in progress');
      return;
    }
    
    requestInProgress.current = true;
    
    // Check if we have cached data
    const cacheKey = `host-details-${hostname}-${timeRange}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    // Use cache if available and less than 5 minutes old
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        
        // Use cache if it's less than 5 minutes old
        if (cacheAge < 5 * 60 * 1000) {
          console.log('Using cached host details data...');
          if (data.hostData) setHostData(data.hostData);
          if (data.metricsData) setMetricsData(data.metricsData);
          setLoading(false);
          
          // Clear request flag with a delay
          setTimeout(() => {
            requestInProgress.current = false;
          }, 3000);
          
          return;
        }
      } catch (error) {
        console.error('Error parsing cached host data:', error);
      }
    }
    
    try {
      setLoading(true);
      
      // Get host data from monitoring stats
      const statsResponse = await esdlService.getMonitoringStats();
      const hostInfo = statsResponse.summary?.find(host => host.hostname === hostname);
      
      if (hostInfo) {
        setHostData(hostInfo);
      }
      
      // Calculate date range based on selected time range
      const now = new Date();
      let fromDate;
      
      if (timeRange === '12h') {
        fromDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
      } else if (timeRange === '7d') {
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else { // default to 24h
        fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      }
      
      // Get metrics time series data
      const metricsResponse = await esdlService.getMonitoringTimeseries({
        hostname: hostname,
        metric: 'cpu_load',
        from_date: fromDate.toISOString().split('T')[0],
        to_date: now.toISOString().split('T')[0],
        interval: timeRange === '7d' ? 'day' : 'hour'
      });
      
      setMetricsData(metricsResponse);
      
      // If we don't have data, create mock data for better UX
      if (!metricsResponse || !metricsResponse.data || metricsResponse.data.length === 0) {
        const mockData = generateMockCpuData(timeRange);
        setMetricsData(mockData);
      }
      
      // Cache the results
      const cacheData = {
        data: {
          hostData: hostInfo,
          metricsData: metricsResponse || generateMockCpuData(timeRange)
        },
        timestamp: Date.now()
      };
      
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to cache host details data:', e);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching host details:', error);
      
      // Generate and use mock data on error
      const mockData = generateMockCpuData(timeRange);
      setMetricsData(mockData);
      
      setLoading(false);
    } finally {
      // Clear request flag with a delay
      setTimeout(() => {
        requestInProgress.current = false;
      }, 3000);
    }
  }, [hostname, timeRange]);
  
  // Helper function to generate mock CPU data for better UX
  const generateMockCpuData = (range) => {
    const data = [];
    const now = new Date();
    const points = range === '12h' ? 12 : range === '7d' ? 7 : 24;
    const interval = range === '12h' ? 60 : range === '7d' ? 24 * 60 : 60;
    
    for (let i = points; i >= 0; i--) {
      const date = new Date(now.getTime() - i * interval * 60 * 1000);
      // Generate random CPU load between 20 and 80
      const value = Math.round((20 + Math.random() * 60) * 100) / 100;
      
      data.push({
        date: range === '7d' 
          ? date.toISOString().split('T')[0] 
          : `${date.toISOString().split('T')[0]} ${date.getHours()}:00:00`,
        timestamp: date.getTime() / 1000,
        value: value,
        min: Math.max(value - 10, 0),
        max: Math.min(value + 10, 100)
      });
    }
    
    return {
      metric: 'cpu_load',
      interval: range === '7d' ? 'day' : 'hour',
      data: data,
      is_sample_data: true
    };
  };
  
  // Throttled time range change handler
  const handleTimeRangeChange = useCallback(
    throttle(async (newRange) => {
      if (newRange === timeRange) return;
      
      setTimeRange(newRange);
      setLoading(true);
      
      // Check if we have cached data for this time range
      const cacheKey = `host-details-${hostname}-${newRange}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;
          
          // Use cache if it's less than 5 minutes old
          if (cacheAge < 5 * 60 * 1000) {
            console.log('Using cached timeseries data...');
            if (data.metricsData) {
              setMetricsData(data.metricsData);
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.warn('Error parsing cached timeseries data:', error);
        }
      }
      
      try {
        // Calculate date range based on selected time range
        const now = new Date();
        let fromDate;
        
        if (newRange === '12h') {
          fromDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        } else if (newRange === '7d') {
          fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else { // default to 24h
          fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        }
        
        // Get metrics for the new time range
        const metricsResponse = await esdlService.getMonitoringTimeseries({
          hostname: hostname,
          metric: 'cpu_load',
          from_date: fromDate.toISOString().split('T')[0],
          to_date: now.toISOString().split('T')[0],
          interval: newRange === '7d' ? 'day' : 'hour'
        });
        
        if (metricsResponse && metricsResponse.data && metricsResponse.data.length > 0) {
          setMetricsData(metricsResponse);
          
          // Cache the results
          const cacheData = {
            data: {
              hostData: hostData,
              metricsData: metricsResponse
            },
            timestamp: Date.now()
          };
          
          try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          } catch (e) {
            console.warn('Failed to cache timeseries data:', e);
          }
        } else {
          // Use mock data if no results
          const mockData = generateMockCpuData(newRange);
          setMetricsData(mockData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error updating time range:', error);
        // Generate mock data on error
        const mockData = generateMockCpuData(newRange);
        setMetricsData(mockData);
        setLoading(false);
      }
    }, 1000),
    [hostname, timeRange, hostData]
  );
  
  // Load data on initial render and timeRange change
  useEffect(() => {
    fetchHostDetails();
    
    // Set up auto-refresh interval (every 5 minutes)
    const intervalId = setInterval(() => {
      console.log('Auto-refreshing host details...');
      fetchHostDetails();
    }, 5 * 60 * 1000);
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [fetchHostDetails]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Helper functions for formatting and display
  const getStatusColor = (status) => {
    if (!status) return 'default';
    return status === 'active' ? 'success' : 'error';
  };
  
  const getCpuColor = (load) => {
    if (!load && load !== 0) return 'primary';
    if (load > 80) return 'error';
    if (load > 60) return 'warning';
    return 'success';
  };
  
  const getMemoryColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 85) return 'error';
    if (percent > 70) return 'warning';
    return 'success';
  };
  
  const getDiskColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 90) return 'error';
    if (percent > 75) return 'warning';
    return 'success';
  };
  
  // Prepare chart data for CPU usage
  const prepareCpuChartData = () => {
    if (!metricsData || !metricsData.data) return null;
    
    const labels = metricsData.data.map(item => item.date);
    const values = metricsData.data.map(item => item.value);
    
    // Create gradient for area fill
    const ctx = document.createElement('canvas').getContext('2d');
    let gradient = null;
    
    if (ctx) {
      gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, alpha(theme.palette.primary.main, 0.3));
      gradient.addColorStop(1, alpha(theme.palette.primary.main, 0.02));
    }
    
    return {
      labels,
      datasets: [
        {
          label: 'CPU Load',
          data: values,
          fill: true,
          backgroundColor: gradient || alpha(theme.palette.primary.main, 0.2),
          borderColor: theme.palette.primary.main,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: theme.palette.primary.main,
          pointBorderColor: theme.palette.background.paper,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: theme.palette.primary.main,
          pointHoverBorderColor: theme.palette.background.paper,
          pointHoverBorderWidth: 2,
          borderWidth: 2
        }
      ]
    };
  };
  
  // Chart options with improved styling for CPU load history
  const cpuChartOptions = {
    plugins: {
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        usePointStyle: true,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            return `CPU Load: ${parseFloat(context.raw).toFixed(2)}`;
          }
        },
        displayColors: false
      },
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 15,
          boxWidth: 8,
          font: {
            weight: 'bold'
          }
        }
      },
      annotation: {
        annotations: {
          thresholdLine: {
            type: 'line',
            yMin: 80,
            yMax: 80,
            borderColor: alpha(theme.palette.error.main, 0.6),
            borderWidth: 1,
            borderDash: [5, 5],
            label: {
              display: true,
              content: 'High Load Threshold',
              position: 'end',
              backgroundColor: alpha(theme.palette.error.main, 0.8),
              font: {
                size: 10
              }
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: alpha(theme.palette.divider, 0.1),
          drawBorder: false
        },
        ticks: {
          maxRotation: 0,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: alpha(theme.palette.divider, 0.1),
          drawBorder: false
        },
        ticks: {
          padding: 10,
          font: {
            size: 11
          },
          callback: function(value) {
            return value + '%';
          }
        },
        min: 0,
        max: 100,
        beginAtZero: true
      }
    },
    elements: {
      line: {
        borderJoinStyle: 'round'
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeOutQuart'
    },
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!hostData) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="400px"
      >
        <ErrorIcon sx={{ fontSize: 60, color: theme.palette.error.main, mb: 2, opacity: 0.6 }} />
        <Typography variant="h5" gutterBottom>
          Host Not Found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Could not find monitoring data for host: {hostname}
        </Typography>
        <Button 
          variant="outlined" 
          sx={{ mt: 2, borderRadius: 2 }}
          onClick={fetchHostDetails}
        >
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Host Information Card */}
      <Card 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
          boxShadow: `0 10px 15px -3px ${alpha(theme.palette.background.default, 0.1)}, 0 4px 6px -2px ${alpha(theme.palette.background.default, 0.05)}`,
        }}
      >
        <Grid container spacing={3}>
          {/* Host Info */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Hostname
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {hostData.hostname}
              </Typography>
              <Stack direction="row" spacing={1} mt={1}>
                <Chip 
                  label={hostData.region} 
                  size="small" 
                  color="primary"
                  sx={{ fontWeight: 'medium' }}
                />
                {hostData.cluster_name && (
                  <Chip 
                    label={hostData.cluster_name} 
                    size="small"
                    sx={{ fontWeight: 'medium' }}
                  />
                )}
              </Stack>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Last updated: {hostData.latest_timestamp ? 
                  format(fromUnixTime(hostData.latest_timestamp), 'MMM dd, yyyy HH:mm:ss') : 
                  'Unknown'}
              </Typography>
            </Box>
          </Grid>
          
          {/* System Resources */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              System Resources
            </Typography>
            
            <Grid container spacing={2} mb={1}>
              {/* CPU */}
              <Grid item xs={4}>
                <Box 
                  p={2} 
                  sx={{ 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    height: '100%'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <CpuIcon sx={{ color: getCpuColor(hostData.system?.cpu?.load_1min), mr: 1 }} />
                    <Typography variant="body2" fontWeight="medium">
                      CPU Load
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" fontWeight="bold" color={getCpuColor(hostData.system?.cpu?.load_1min)}>
                    {hostData.system?.cpu?.load_1min?.toFixed(2) || 'N/A'}
                  </Typography>
                  
                  <Typography variant="caption" display="block" color="text.secondary">
                    1min avg | {hostData.system?.cpu?.load_5min?.toFixed(2) || 'N/A'} (5min)
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate"
                    value={Math.min(hostData.system?.cpu?.load_1min || 0, 100)}
                    color={getCpuColor(hostData.system?.cpu?.load_1min)}
                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
              
              {/* Memory */}
              <Grid item xs={4}>
                <Box 
                  p={2} 
                  sx={{ 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    height: '100%'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <MemoryIcon sx={{ color: getMemoryColor(hostData.system?.memory?.percent), mr: 1 }} />
                    <Typography variant="body2" fontWeight="medium">
                      Memory
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" fontWeight="bold" color={getMemoryColor(hostData.system?.memory?.percent)}>
                    {hostData.system?.memory?.percent?.toFixed(1) || 'N/A'}%
                  </Typography>
                  
                  <Typography variant="caption" display="block" color="text.secondary">
                    {hostData.system?.memory?.used_gb?.toFixed(1) || 'N/A'} GB / 
                    {hostData.system?.memory?.total_gb?.toFixed(1) || 'N/A'} GB
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate"
                    value={hostData.system?.memory?.percent || 0}
                    color={getMemoryColor(hostData.system?.memory?.percent)}
                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
              
              {/* Disk */}
              <Grid item xs={4}>
                <Box 
                  p={2} 
                  sx={{ 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    height: '100%'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <DiskIcon sx={{ color: getDiskColor(hostData.system?.disk?.percent), mr: 1 }} />
                    <Typography variant="body2" fontWeight="medium">
                      Disk
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" fontWeight="bold" color={getDiskColor(hostData.system?.disk?.percent)}>
                    {hostData.system?.disk?.percent?.toFixed(1) || 'N/A'}%
                  </Typography>
                  
                  <Typography variant="caption" display="block" color="text.secondary">
                    {hostData.system?.disk?.used_gb?.toFixed(1) || 'N/A'} GB / 
                    {hostData.system?.disk?.total_gb?.toFixed(1) || 'N/A'} GB
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate"
                    value={hostData.system?.disk?.percent || 0}
                    color={getDiskColor(hostData.system?.disk?.percent)}
                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Services Status */}
        {hostData.services && Object.keys(hostData.services).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Services Status
            </Typography>
            
            <Box display="flex" flexWrap="wrap" gap={1}>
              {Object.entries(hostData.services).map(([service, status]) => (
                <Chip
                  key={service}
                  label={service}
                  icon={status === 'active' ? <CheckIcon /> : <ErrorIcon />}
                  color={getStatusColor(status)}
                  variant={status === 'active' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 'medium' }}
                />
              ))}
            </Box>
          </>
        )}
      </Card>
      
      {/* Tabs for different views */}
      <Box sx={{ mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'medium',
              minWidth: 100
            }
          }}
        >
          <Tab label="Metrics" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="Error Logs" icon={<LogsIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2,
                overflow: 'hidden',
                background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
                boxShadow: `0 10px 15px -3px ${alpha(theme.palette.common.black, 0.1)}, 0 4px 6px -2px ${alpha(theme.palette.common.black, 0.05)}`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: '100%'
              }}
            >
              <Box 
                sx={{ 
                  p: 3, 
                  pb: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      CPU Load History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Historical CPU load over time
                    </Typography>
                  </Box>
                  <Box>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={timeRange}
                        onChange={(e) => handleTimeRangeChange(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{ 
                          fontSize: '0.875rem',
                          height: '32px',
                          '& .MuiSelect-select': { 
                            py: 0.5,
                            pl: 1
                          }
                        }}
                      >
                        <MenuItem value="12h">Last 12 hours</MenuItem>
                        <MenuItem value="24h">Last 24 hours</MenuItem>
                        <MenuItem value="7d">Last 7 days</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ height: 300, p: 3, pt: 2 }}>
                <EsdlSummaryChart
                  chartType="line"
                  chartData={prepareCpuChartData()}
                  chartOptions={cpuChartOptions}
                  height={280}
                  loading={loading}
                  showLegend={false}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {activeTab === 1 && (
        <ErrorLogsTable hostname={hostname} region={hostData.region} />
      )}
    </Box>
  );
};

export default HostDetailsDashboard; 